/* eslint-disable no-param-reassign */
/* global ActionCable */
import PropTypes from "prop-types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  caseTransformingAxios,
  responseDataToCamelCase,
  requestDataToSnakeCase,
} from "@shared/v2/caseTransformingAxios";
import dncApi from "@shared/phoneDncStatusApi";
import { upperFirst } from "lodash";
import { viewPhoneFromFlat } from "@shared/models/Communications/viewPhone";
import { STATUS_ICON_MODIFIERS } from "@shared/models/Communications/viewPhoneIcons/CallStatusIcon";
import { DncFlag } from "@shared/Sms";
import CALL_STATES from "../../Dialer/utils/states";
import { updateTimeline } from "../../PersonDetail/actions/personDetailActionCreators";

export const DIALER_NAME = "dialer";
export const DIALER_INLINE_NAME = "dialerInline";

export const initialState = {
  callState: CALL_STATES.Init,
  isRecording: false,
  isRecordingConsentRequired: false,
  comment: "",
  conference: null,
  conferenceCallLeadUuids: null,
  conferenceCallSource: undefined,
  duration: null,
  error: null,
  leadUuids: null,
  loading: false,
  outcome: null,
  person: {},
  phoneNumber: null,
  phoneNumbers: [],
  session: null,
  validLeads: {},
};

const PhoneNumberOption = ({ viewPhone, isDnc }) => {
  const icon = viewPhone.isMobile ? viewPhone.mobileCallIcon : viewPhone.callIcon;
  const phoneColor = STATUS_ICON_MODIFIERS[viewPhone.callStatus];

  return (
    <div className="tw-flex tw-space-x-[6px] tw-items-center">
      <span className="tw-flex">{icon.icon({ size: "m" })}</span>
      <span className={phoneColor}>{`${upperFirst(viewPhone.category)}: ${viewPhone.formattedNumber}`}</span>
      {isDnc && <DncFlag show={isDnc} />}
    </div>
  );
};

PhoneNumberOption.propTypes = {
  viewPhone: PropTypes.shape().isRequired,
  isDnc: PropTypes.bool.isRequired,
};

const checkValidLeads = (name) =>
  createAsyncThunk(`${name}/checkValidLeads`, (leadUuids) =>
    caseTransformingAxios.post("/dialer/check_valid_leads", { leadUuids }),
  );


const getPersonAndNumbers = (name) =>
  createAsyncThunk(`${name}/getPerson`, (uuid, thunkApi) =>
    caseTransformingAxios
      .get(`/api/v4/person/${uuid}/dialer_details`, { signal: thunkApi.signal })
      .then(({ data: person }) => {
        const dialablePhoneNumbers = person?.dialablePhoneNumbers || [];
        return dncApi
          .index(
            dialablePhoneNumbers.map((x) => x.value),
            thunkApi.signal,
          )
          .catch(() => ({ data: {} }))
          .then(({ data: dncMap }) => {
            const phoneNumbers = dialablePhoneNumbers.map((phone) => {
              const viewPhone = viewPhoneFromFlat(phone);
              const isDnc = dncMap[phone.value];
              return {
                label: <PhoneNumberOption viewPhone={viewPhone} isDnc={isDnc} />,
                value: phone.value,
                meta: { ...viewPhone, isDnc },
              };
            });
            const phoneNumber = phoneNumbers.find((i) => i.meta.position === 1) || phoneNumbers[0] || null;
            return {
              person: {
                id: person.id,
                fullName: person.fullName,
                primaryPhoneNumber: phoneNumber?.value,
                uuid: person.uuid,
                slug: person.slug,
              },
              phoneNumbers,
              phoneNumber,
            };
          });
      }),
  );

const createDialerSession = (name) =>
  createAsyncThunk(`${name}/createDialerSession`, (_, thunkApi) => {
    const {
      [name]: { leadUuids, person, listingUuid, callSource },
      layout: {
        apiUrls: { dialerServiceUrl },
      },
    } = thunkApi.getState();

    return caseTransformingAxios
      .post(
        "/dialer/create_dialer_session",
        { leadUuids, person, listingUuid, callSource },
        { signal: thunkApi.signal },
      )
      .then(({ data: session }) =>
        caseTransformingAxios
          .post(`${dialerServiceUrl}/dialer/start_dialer_session`, { token: session.token })
          .then(() => session),
      )
      .catch((err) => thunkApi.rejectWithValue(err.response?.data));
  });

const endCall = (name) =>
  createAsyncThunk(`${name}/dialer/endCall`, (_, thunkApi) => {
    const {
      [name]: { person, session },
      layout: {
        apiUrls: { dialerServiceUrl },
      },
    } = thunkApi.getState();
    const token = session?.token;
    return caseTransformingAxios
      .post(`${dialerServiceUrl}/dialer/end_current_call`, { token }, { signal: thunkApi.signal })
      .catch(console.log)
      .finally(() => {
        if (person) {
          return caseTransformingAxios
            .post(`${dialerServiceUrl}/dialer/end_dialer_session`, { token })
            .catch(console.log);
        }
        return undefined;
      });
  });

const logCall = (name) =>
  createAsyncThunk(`${name}/logCall`, ({ listingId, listingUuid } = {}, thunkApi) => {
    const {
      [name]: { outcome, comment, conference, person, session },
      layout: {
        currentUser,
        apiUrls: { dialerServiceUrl },
      },
    } = thunkApi.getState();
    const token = session?.token;

    if (!token || !conference?.currentParticipant?.id) {
      return caseTransformingAxios
        .post(`/people/${person.id}/interactions`, {
          interaction: {
            listingId,
            userId: currentUser.id,
            personId: person.id,
            interactionType: "call",
            comment,
            outcome,
          },
          personDetailsPage: "true",
        })
        .then((res) =>
          thunkApi.dispatch(
            updateTimeline(requestDataToSnakeCase(res.data), "UPDATE_PUBLIC_ACTIVITY_EVENTS"),
          ),
        );
    }

    return caseTransformingAxios
      .post(`${dialerServiceUrl}/dialer/save_outcome`, {
        outcome,
        comment,
        listingUuid,
        participantId: conference?.currentParticipant.id,
        token,
      })
      .then(() => {
        const consumer = ActionCable.createConsumer("/cable");

        const subscription = consumer.subscriptions.create(
          { channel: "InteractionsChannel" },
          {
            connected() {
              this.perform("follow", { call_sid: conference.currentParticipant.callSid });
            },
            received(data) {
              thunkApi.dispatch(
                updateTimeline(data, "UPDATE_PUBLIC_ACTIVITY_EVENTS"),
              );

              subscription.unsubscribe();
            }
          }
        );
      });
  });

const startRecording = (name) =>
  createAsyncThunk(`${name}/startRecording`, ({ recordingConsent, isDismissed }, thunkApi) => {
    const {
      [name]: { session },
      layout: {
        apiUrls: { dialerServiceUrl },
      },
    } = thunkApi.getState();
    const token = session?.token;
    return caseTransformingAxios
      .post(`${dialerServiceUrl}/dialer/start_recording`, { token, recording_consent: recordingConsent, recording_dismissed: isDismissed })
      .then(() => recordingConsent)
      .catch((err) => thunkApi.rejectWithValue(err.response?.data));
  });

const stopRecording = (name) =>
  createAsyncThunk(`${name}/stopRecording`, (_, thunkApi) => {
    const {
      [name]: { session },
      layout: {
        apiUrls: { dialerServiceUrl },
      },
    } = thunkApi.getState();
    const token = session?.token;
    return caseTransformingAxios.post(`${dialerServiceUrl}/dialer/stop_recording`, { token });
  });

const sharedSlice = (name = DIALER_NAME) => ({
  name,
  initialState,
  reducers: {
    clearSession: (state) => {
      state.session = null;
    },
    setCallState: (state, action) => {
      state.callState = action.payload;
    },
    setPhoneNumber: (state, action) => {
      state.phoneNumber = action.payload;
      state.person.primaryPhoneNumber = action.payload.value;
    },
    actionCableUpdateReceived: (state, action) => {
      const conference = responseDataToCamelCase(action.payload.conference);
      if (conference.currentParticipant?.humanizedCallStatus === "Ringing") {
        state.callState = CALL_STATES.Ringing;
      }
      if (conference.currentParticipant?.humanizedCallStatus === "In-progress") {
        state.callState = CALL_STATES.Connected;
      }
      if (
        conference.totalParticipants === 1 &&
        (conference.currentParticipant?.humanizedCallStatus === "Completed" ||
          conference.agentParticipant?.humanizedCallStatus === "Completed")
      ) {
        state.callState = CALL_STATES.Ended;
      }
      if (conference.agentParticipant?.humanizedCallStatus === "Canceled") {
        state.callState = CALL_STATES.Init;
        state.session = null;
      }
      state.conference = conference;
    },
    setCallOutcome: (state, action) => {
      state.outcome = action.payload;
    },
    setComment: (state, action) => {
      state.comment = action.payload;
    },
    setDuration: (state, action) => {
      state.duration = action.payload;
    },
    setCallParams: (state, action) => {
      if (action.payload) {
        state.leadUuids = action.payload.leadUuids;
        state.callSource = action.payload.callSource;
        state.listingUuid = action.payload.listingUuid;
      } else {
        state.leadUuids = null;
        state.callSource = undefined;
        state.listingUuid = undefined;
      }
    },
    setConferenceCallParams: (state, action) => {
      if (action.payload) {
        state.conferenceCallLeadUuids = action.payload.leadUuids;
        state.conferenceCallSource = action.payload.callSource;
      } else {
        state.conferenceCallLeadUuids = null;
        state.conferenceCallSource = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkValidLeads(name).pending, (state) => {
        state.loading = true;
      })
      .addCase(checkValidLeads(name).fulfilled, (state, action) => {
        state.loading = false;
        state.validLeads = action.payload;
      })
      .addCase(checkValidLeads(name).rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(getPersonAndNumbers(name).pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getPersonAndNumbers(name).fulfilled, (state, action) => {
        state.callState = CALL_STATES.Init;
        state.loading = false;
        state.person = action.payload.person;
        state.phoneNumbers = action.payload.phoneNumbers;
        state.phoneNumber = action.payload.phoneNumber;
      })
      .addCase(getPersonAndNumbers(name).rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch person and phone numbers. Please try again later.";
      });

    builder
      .addCase(createDialerSession(name).pending, (state) => {
        state.error = null;
        state.isRecording = false;
      })
      .addCase(createDialerSession(name).fulfilled, (state, action) => {
        state.session = action.payload;
      })
      .addCase(createDialerSession(name).rejected, (state, action) => {
        state.callState = CALL_STATES.Init;
        if (action.payload?.status === 422) {
          state.error = action.payload.error;
        } else {
          state.error = "Dialer session could not be created, try again later.";
        }
      })
      .addCase(startRecording(name).pending, (state) => {
        state.isRecording = true;
        state.isRecordingConsentRequired = false;
      })
      .addCase(startRecording(name).rejected, (state, action) => {
        state.isRecording = false;
        if (action.payload?.error === "Recording consent is required") {
          state.isRecordingConsentRequired = true;
        }
      })
      .addCase(startRecording(name).fulfilled, (state) => {
        state.isRecording = true;
        state.isRecordingConsentRequired = false;
      })
      .addCase(stopRecording(name).pending, (state) => {
        state.isRecording = false;
      });

    builder
      .addCase(logCall(name).fulfilled, (state) => {
        state.outcome = null;
        state.comment = "";
        state.callState = CALL_STATES.Init;
        state.session = null;
        state.isRecording = false;
      })
      .addCase(logCall(name).rejected, (state, action) => {
        console.log(action.error);
        state.error = "Call outcome could not be saved.";
      });
  },
});

const dialerSlice = createSlice(sharedSlice());

const dialerInlineSlice = createSlice(sharedSlice(DIALER_INLINE_NAME));

export {
  checkValidLeads,
  createDialerSession,
  endCall,
  getPersonAndNumbers,
  logCall,
  startRecording,
  stopRecording,
};

export const {
  actionCableUpdateReceived,
  setCallOutcome,
  setCallState,
  setDuration,
  setComment,
  setPhoneNumber,
  clearSession,
  setInline,
  setCallParams,
  setConferenceCallParams,
} = dialerSlice.actions;

export const {
  actionCableUpdateReceived: actionCableUpdateReceivedInline,
  setCallOutcome: setCallOutcomeInline,
  setCallState: setCallStateInline,
  setDuration: setDurationInline,
  setComment: setCommentInline,
  setPhoneNumber: setPhoneNumberInline,
  clearSession: clearSessionInline,
  setInline: setInlineInline,
  setCallParams: setCallParamsInline,
} = dialerInlineSlice.actions;

export const dialer = dialerSlice.reducer;
export const dialerInline = dialerInlineSlice.reducer;
